import React, { useEffect, useState } from "react";
import { Button, Text, Loader, LinearProgressBar } from "@vibe/core";
import useSubitemBulkScan from "../../hooks/api/useSubitemBulkScan";
import { BulkSummary, BulkWriteReport } from "../../hooks/api/subitemBulk.types";
import BulkConfirmModal from "./BulkConfirmModal";
import {
  boardLabel,
  boardsPhrase,
  columnTitleFromKey,
  describeElapsed,
  describeRemaining,
  explainFailure,
  plural,
} from "./resyncCopy";

/**
 * Re-renders the panel once a minute, so the "started 6 minutes ago" / "finished 9 minutes ago"
 * lines keep up on surfaces that do not otherwise re-render: `ready` has stopped polling (its
 * status is terminal), and a busy-elsewhere panel re-renders only when the lookup's answer changes.
 *
 * A TRIGGER ONLY. It deliberately does not hand back the time it ticked at: the panel reads
 * `Date.now()` at render instead. The first version returned its own minute-old timestamp and the
 * scanning surface computed its estimate from it — while progress arrived every two seconds, so
 * within each minute the elapsed time stood still as the checked count grew, the estimate slid
 * from 10 minutes to 1, and then leapt back up on the next tick. A 4,000-item board made that
 * sawtooth impossible to miss.
 */
const useMinuteRerender = (): void => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((tick) => tick + 1), 60_000);
    return () => clearInterval(timer);
  }, []);
};

/**
 * The scan's progress, as a bar and a count, or nothing when the record carries no progress yet.
 * `LinearProgressBar` is Vibe's, so the bar follows the theme; `indicateProgress` is off because
 * the count beside it is the number the user actually wants ("312 of 1,312 items"), and a bare
 * percentage next to it would be the same fact twice.
 */
const ScanProgress = ({
  progress,
}: {
  progress: { parentsChecked: number; parentsTotal: number } | undefined;
}) => {
  if (!progress || progress.parentsTotal <= 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-xs)" }}>
      <LinearProgressBar
        value={progress.parentsChecked}
        max={progress.parentsTotal}
        size="small"
        ariaLabel={`${progress.parentsChecked} of ${progress.parentsTotal} items checked`}
      />
      <Text type="text3" color="secondary" element="p">
        {`${progress.parentsChecked.toLocaleString()} of ${progress.parentsTotal.toLocaleString()} ${
          progress.parentsTotal === 1 ? "item" : "items"
        } checked`}
      </Text>
    </div>
  );
};

const CONTAINER_STYLE: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--spacing-medium)",
  padding: "var(--spacing-large)",
};

const ROW_STYLE: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "var(--spacing-small)",
};

/**
 * The idle phase only. Every other phase renders as a block (CONTAINER_STYLE);
 * idle renders as a single row that costs one line above the table.
 *
 * HUMAN DECISION (Mike, 2026-08-19), chosen from four mocked placements: the
 * panel is a collapsed strip that expands in place. It stays discoverable
 * without costing a block of vertical space on every visit, and the region it
 * expands into is where the confirmation step lives. Reaffirmed 2026-09-04
 * (option A1): the ready state stays a few lines tall, and the detail lives in
 * the dialog.
 */
const IDLE_STRIP_STYLE: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "var(--spacing-medium)",
  padding: "var(--spacing-small) var(--spacing-medium)",
};

const NOTICE_STYLE: React.CSSProperties = {
  padding: "var(--spacing-small) var(--spacing-medium)",
  borderRadius: "var(--border-radius-small)",
};

const WARN_NOTICE: React.CSSProperties = { ...NOTICE_STYLE, background: "var(--warning-color-selected)" };
const BAD_NOTICE: React.CSSProperties = { ...NOTICE_STYLE, background: "var(--negative-color-selected)" };
const GOOD_NOTICE: React.CSSProperties = { ...NOTICE_STYLE, background: "var(--positive-color-selected)" };
const QUIET_NOTICE: React.CSSProperties = { ...NOTICE_STYLE, background: "var(--primary-background-hover-color)" };

const BUTTON_ROW_STYLE: React.CSSProperties = { display: "flex", gap: "var(--spacing-small)" };

type SkipReason = BulkSummary["skipped"][number]["reason"];

/**
 * What each skip reason means to the user, as an instruction rather than an enum.
 *
 * "deferred" and "unverified" are permanent properties of a parent (too large to scan in a
 * page; some of its boards unreadable), and the Subitems table already shows those parents by
 * name with their own per-parent rescan action — so the panel points there. "scan_failed" is
 * a transient bulk-only infrastructure failure with no per-parent affordance anywhere else,
 * which is why it is the one reason that gets a retry control here.
 */
const skipSentence = (reason: SkipReason, count: number): string => {
  const items = plural(count, "item");
  const them = count === 1 ? "it" : "them";
  switch (reason) {
    case "deferred":
      return `${items} ${count === 1 ? "was" : "were"} too large to include in this scan. Find ${them} in the list below and scan ${them} individually.`;
    case "unverified":
      return `${items} could not be fully checked because some of ${count === 1 ? "its" : "their"} boards were unreachable or aren't visible to you. Find ${them} in the list below to see which.`;
    case "scan_failed":
      return `${items} could not be read. Scanning again may pick ${them} up.`;
    default:
      return `${items} could not be included in this scan.`;
  }
};

// Counted rather than listed one row per parentSyncId: the id is an internal
// detail this panel has no business surfacing, but every DISTINCT reason a
// parent was skipped for must still be visible, not folded into a single
// "some parents were skipped" that hides which failure mode occurred.
const summarizeSkips = (skipped: BulkSummary["skipped"]): Array<{ reason: SkipReason; count: number }> => {
  const counts = new Map<SkipReason, number>();
  skipped.forEach(({ reason }) => counts.set(reason, (counts.get(reason) ?? 0) + 1));
  return Array.from(counts.entries()).map(([reason, count]) => ({ reason, count }));
};

/**
 * "Acme renewal › Kickoff call › Status on Client Deliverables" — the failing cell, named by
 * the labels the user knows it by. Every part is optional on the wire (a plan stored before
 * names existed carries none; a whole-call failure has no board), and each is simply left out
 * rather than rendered as an id or the string "undefined".
 */
export const failurePath = (
  failure: BulkWriteReport["failed"][number],
  boardNames: Record<string, string> | undefined,
): string => {
  const parts = [
    failure.parentName,
    failure.subitemName,
    failure.columnTitle ?? (failure.columnKey ? columnTitleFromKey(failure.columnKey) : undefined),
  ].filter((part): part is string => Boolean(part && part.trim()));
  const what = parts.length > 0 ? parts.join(" › ") : failure.kind === "parentItem" ? "An item" : "A subitem";
  return failure.boardId ? `${what} on ${boardLabel(boardNames, failure.boardId)}` : what;
};

/** Failures shown before the list folds behind "Show more". */
const FAILURES_SHOWN_BY_DEFAULT = 5;

/**
 * Everything a report says apart from its headline, rendered identically by the
 * `completed` and `failed` surfaces.
 *
 * Shared because a FAILED run's partial report is exactly as consequential as a completed
 * one's — the same irreversible cells, the same possibly-duplicated labels, the same per-cell
 * refusals — and two copies would let the failure surface quietly fall behind the success one.
 */
const ReportDetails = ({ report, boardNames }: { report: BulkWriteReport; boardNames?: Record<string, string> }) => {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? report.failed : report.failed.slice(0, FAILURES_SHOWN_BY_DEFAULT);
  const hidden = report.failed.length - visible.length;

  return (
    <>
      {report.failed.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-small)" }}>
          {/*
            "changes", not "values": these entries are not in the cell unit the counts above
            are in. `columnKey` present means one CELL failed, absent means a whole
            `(entity, board)` write did, so this length is not subtractable from `succeeded`
            and must not be worded as if it were. `succeeded` and `failed` are also NOT
            mutually exclusive (Phase 3a Decision 8), so this list is never collapsed to a count.
          */}
          <div style={BAD_NOTICE}>
            <Text type="text2" element="p">
              {`${plural(report.failed.length, "change")} could not be made. Everything else was, and cannot be undone.`}
            </Text>
          </div>
          <ul style={{ margin: 0, paddingInlineStart: "var(--spacing-medium)" }}>
            {visible.map((failure, index) => {
              const explained = explainFailure(failure.reason);
              return (
                <li key={`${failure.parentSyncId ?? ""}-${failure.subitemSyncId ?? ""}-${failure.boardId}-${failure.columnKey ?? "board"}-${index}`}>
                  <Text type="text2" weight="medium" element="div">
                    {failurePath(failure, boardNames)}
                  </Text>
                  <Text type="text2" color="secondary" element="div">
                    {explained.message}
                  </Text>
                  {explained.nextStep ? (
                    <Text type="text2" element="div">
                      {explained.nextStep}
                    </Text>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {hidden > 0 ? (
            <div>
              <Button size="small" kind="tertiary" onClick={() => setShowAll(true)}>
                {`Show ${hidden} more`}
              </Button>
            </div>
          ) : null}
        </div>
      )}
      {/*
        The label cache has a 600s TTL, so a run that hit a stale entry may have created
        DUPLICATE labels on a customer's board rather than reusing the existing one — see
        resync-value-translator.ts's `labelsWrittenByText` header. Shown only when it happened
        (Mike, 2026-09-04, reversing the earlier always-shown rule): a report that warns about
        duplicate labels on every clean run trains a reader to skip the warning.
      */}
      {report.labelsWrittenByText > 0 && (
        <div style={QUIET_NOTICE}>
          <Text type="text2" element="p">
            {`${plural(report.labelsWrittenByText, "label was", "labels were")} matched by name rather than by id. If a board now shows the same label twice, delete the newer one.`}
          </Text>
        </div>
      )}
    </>
  );
};

/** A token-styled bar. Vibe's own is a heavier component than a two-colour strip warrants here. */
const ProgressBar = ({ percent, tone }: { percent: number; tone: "positive" | "primary" }) => (
  <div
    role="progressbar"
    aria-valuenow={percent}
    aria-valuemin={0}
    aria-valuemax={100}
    style={{
      height: 6,
      borderRadius: 3,
      background: "var(--primary-background-hover-color)",
      overflow: "hidden",
    }}
  >
    <div style={{ width: `${percent}%`, height: "100%", background: `var(--${tone}-color)` }} />
  </div>
);

/**
 * Everything the panel needs from the job machine — exactly what
 * `useSubitemBulkScan` returns, named so a caller can hoist the hook and hand
 * the same instance to more than one mount of this panel.
 */
export type BulkScanController = ReturnType<typeof useSubitemBulkScan>;

const BulkScanSummary = ({
  currentBoard,
  token,
  controller,
}: {
  currentBoard: number | null;
  token: string | null;
  /**
   * A job machine owned by the CALLER, shared across every mount of this panel.
   *
   * The panel renders in both tabs, and Vibe's TabPanels mounts only the active
   * one — so without a shared controller each tab would own a separate `jobId`,
   * and switching tabs mid-run would drop the running job on unmount. The lock
   * behind it is per ACCOUNT, so the other tab's idle strip would then answer
   * `busy` and the summary would be reachable from neither tab.
   *
   * Optional so this component still stands alone (its own tests render it
   * without one), which is why the hook below is called unconditionally and
   * then discarded when a controller is supplied — Rules of Hooks first, and a
   * discarded instance is inert: it starts nothing on its own.
   */
  controller?: BulkScanController;
}) => {
  const ownController = useSubitemBulkScan({ currentBoard, token });
  const { state, start, confirm, reset, recheck, rescan } = controller ?? ownController;
  useMinuteRerender();
  // Read at render, never cached: the scanning surface re-renders on every poll and its
  // estimate needs the elapsed time to move with the progress. See `useMinuteRerender`.
  const now = Date.now();

  // Local to this component, not the hook: the dialog is a pure consent gate, so whether it is
  // on screen is UI state, not job state. Nothing in this file may call `confirm()` except
  // `handleConfirm` below, and `handleConfirm` only ever runs from BulkConfirmModal's own
  // primary button — never from the panel's "Review and fix" button, which only opens it.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await confirm();
    } finally {
      setConfirming(false);
      setConfirmOpen(false);
    }
  };

  switch (state.phase) {
    case "idle":
      return (
        <div style={IDLE_STRIP_STYLE}>
          {/*
            "whole board" is load-bearing copy, not emphasis. This strip renders directly
            beneath each tab's "Discrepancies only" filter, and a scan described as covering
            "this board" reads there as covering what the filter is showing. It never does: the
            scan enumerates every parent on the board and ignores the grid's paging and
            filtering entirely. A test pins the phrase.

            "items and subitems", not "subitems": one scan covers both sides, and the same
            panel renders in both tabs.
          */}
          <Text type="text2" color="secondary">
            Scan the whole board for items and subitems whose values have drifted out of sync.
          </Text>
          <Button size="small" onClick={() => start()}>
            Scan for issues
          </Button>
        </div>
      );

    case "starting":
      return (
        <div style={{ ...CONTAINER_STYLE, ...ROW_STYLE }}>
          <Loader size="small" color="primary" />
          <Text type="text2" color="secondary">
            Scanning the whole board for issues&hellip;
          </Text>
        </div>
      );

    case "scanning": {
      /*
       * The two lines under the headline are the whole point of this hotfix's scanning surface.
       * "Started N minutes ago, about M left" tells a user who has come back to the view that the
       * spinner is THEIR scan, still going, and how long to wait; the notice tells them leaving
       * is safe — which is only true now that a reloaded view reattaches to the job.
       */
      const remaining = describeRemaining(state.progress, state.startedAt, now);
      return (
        <div style={CONTAINER_STYLE}>
          <div style={ROW_STYLE}>
            <Loader size="small" color="primary" />
            <Text type="text1" weight="bold">
              Scanning the whole board for issues&hellip;
            </Text>
          </div>
          {state.startedAt !== undefined && (
            <Text type="text2" color="secondary" element="p">
              {`Started ${describeElapsed(state.startedAt, now)}${remaining ? `, ${remaining} left` : ""}.`}
            </Text>
          )}
          <ScanProgress progress={state.progress} />
          <div style={QUIET_NOTICE}>
            <Text type="text2" element="p">
              You can leave this page. The results will be here when you come back.
            </Text>
          </div>
        </div>
      );
    }

    case "busy": {
      const { elsewhere } = state;
      return (
        <div style={CONTAINER_STYLE}>
          {/*
            Two versions of the same refusal. With `elsewhere`, the lookup could say WHICH job
            holds the account's lock, and it is on another board: name it and say since when.
            Without it, only the 409 is known — say what the lock is (per account) and no more.
            The old copy guessed "it may be on another board" for both; in every logged case it
            was the user's own scan on THIS board, which the hook now adopts before reaching here.
          */}
          {elsewhere ? (
            <>
              <div style={ROW_STYLE}>
                <Loader size="small" color="primary" />
                <Text type="text1" weight="bold">
                  {`A scan is running on ${elsewhere.boardName ?? `board ${elsewhere.boardId}`}`}
                </Text>
              </div>
              {elsewhere.startedAt !== undefined && (
                <Text type="text2" color="secondary" element="p">
                  {`Started ${describeElapsed(elsewhere.startedAt, now)}.`}
                </Text>
              )}
              <ScanProgress progress={elsewhere.progress} />
              <div style={QUIET_NOTICE}>
                <Text type="text2" element="p">
                  Only one scan can run per account at a time. This panel updates itself, and you
                  can scan this board as soon as that one finishes.
                </Text>
              </div>
            </>
          ) : (
            <Text type="text2" color="secondary">
              A scan is already running for your account. Only one can run at a time.
            </Text>
          )}
          {/*
            `recheck`, not `reset`: it keeps the job id, so the scan summary the user waited
            for survives and they land back on the ready state rather than having to scan the
            whole board again.
          */}
          <div>
            <Button onClick={() => recheck()}>Check again</Button>
          </div>
        </div>
      );
    }

    case "failed": {
      const { report } = state;
      return (
        <div style={CONTAINER_STYLE}>
          <Text type="text2" color="negative">
            {state.reason}
          </Text>
          {/*
            A write that failed part-way through has still changed the user's boards,
            irreversibly, and this is the only place they can find out what. Rendered only when
            a report exists, which is the discriminator between a failure DURING the write and
            one during the scan: a scan-stage failure wrote nothing, and "0 values were updated"
            there would still invite an audit of boards that never changed.

            No denominator here, unlike the `completed` headline: this sentence already says
            the run did not finish, so the number's job is to say what changed.
          */}
          {report && (
            <>
              <Text type="text1" element="p">
                {`${plural(report.succeeded, "value was", "values were")} updated before this stopped. Those changes cannot be undone.`}
              </Text>
              <ReportDetails report={report} />
            </>
          )}
          <div>
            <Button onClick={() => reset()}>Try again</Button>
          </div>
        </div>
      );
    }

    case "unknown":
      return (
        <div style={CONTAINER_STYLE}>
          {/*
            NOT `color="negative"`, and the copy does not contain the word "failed". This is
            reached when the confirm request produced no answer, which means the write may be
            running on the user's boards at this moment — the `failed` surface's claim that
            nothing was written is the one sentence that must never appear here.
          */}
          <Text type="text2" color="secondary">
            {state.reason}
          </Text>
          {/*
            `recheck`, never `reset`. The record is the only thing that can say what happened,
            `jobId` is the only route to it, and its report is the only account of an
            irreversible write — so the escape from this surface has to be the one that KEEPS
            the job.
          */}
          <div>
            <Button onClick={() => recheck()}>Check again</Button>
          </div>
        </div>
      );

    case "expired":
      return (
        <div style={CONTAINER_STYLE}>
          <Text type="text2" color="secondary">
            This scan&apos;s result has expired. Start a new scan to see the board&apos;s current
            state.
          </Text>
          {/*
            `rescan`, not `reset`: `reset` only returns the panel to the idle strip, leaving a
            second click the copy says nothing about.
          */}
          <div>
            <Button onClick={() => rescan()}>Start a new scan</Button>
          </div>
        </div>
      );

    case "ready": {
      const { summary } = state;
      // The honesty requirement: a scan that could not read every parent must never present
      // its counts as if it had. `skipped.length > 0` alone is not sufficient: a sync group
      // deleted between enumeration and its chunk's turn simply does not come back — not
      // scanned, not skipped, just absent — so parentsScanned falling short of parentsTotal
      // with nothing in `skipped` to explain the gap is itself proof the scan was partial.
      const wasPartial = summary.skipped.length > 0 || summary.parentsScanned < summary.parentsTotal;
      const nothingToFix = summary.columnWrites === 0;
      /*
       * Parents monday could not return. Absent, or a count of 0, means none, so a frontend
       * deployed ahead of the backend keeps today's copy. `count` is clamped for display as a
       * defensive measure only.
       */
      const monday = summary.skippedItems;
      const hasSkippedItems = (monday?.count ?? 0) > 0;
      const mondayTotal = hasSkippedItems ? monday!.totalRequested : 0;
      const mondaySkipped = hasSkippedItems ? Math.min(monday!.count, mondayTotal) : 0;
      const incomplete = wasPartial || hasSkippedItems;
      const allParentsScanned = summary.parentsScanned >= summary.parentsTotal;

      /*
       * Every parent counted in `skippedItems` is ALSO in `skipped`, as `scan_failed` (its chunk
       * failed) or `unverified` (its chunk reported it unchecked). Listing both would give the
       * same parents two causes, one of them pointing at permissions. So the monday sentence
       * explains them once and the per-reason counts drop them: every `scan_failed`, then the
       * rest of `skippedItems` out of `unverified`. What `unverified` keeps is the stable kind
       * (an archived mirror, a board not visible to the user).
       */
      const scanFailed = summary.skipped.filter((entry) => entry.reason === "scan_failed").length;
      const mondayUnverified = Math.max(0, mondaySkipped - scanFailed);
      const skipCounts = summarizeSkips(summary.skipped)
        .map(({ reason, count }) => {
          if (!hasSkippedItems) return { reason, count };
          if (reason === "scan_failed") return { reason, count: 0 };
          if (reason === "unverified") return { reason, count: Math.max(0, count - mondayUnverified) };
          return { reason, count };
        })
        .filter(({ count }) => count > 0);

      /*
       * Only one side of some parents could be verified, and every parent's other side was read:
       * nothing was left unread, so a count of "items checked" would say 170 of 170 under a
       * headline saying some could not be. Those runs name the gap without a count.
       */
      const onlySidesUnverified = !hasSkippedItems && allParentsScanned;

      /*
       * Both sides of the run, stated separately — never summed. They count different entities
       * on different boards, and a single "N affected" would let a reader on either tab take
       * the whole figure for their own side. The parent clause renders only when
       * `parentsAffected` is present: absent means the scan had no notion of parent-item
       * drift, and a `?? 0` would state "0 items" about a scan that never looked. Two counts
       * joined by "and" are a plural subject, so that form always takes "are".
       *
       * A scan that did not cover every parent and found nothing does not get "Everything is in
       * sync": it has not looked at everything. Falling through to the out-of-sync branch is no
       * better ("0 items and 0 subitems are out of sync"), so it has its own headline.
       */
      const headline =
        nothingToFix && incomplete
          ? onlySidesUnverified
            ? "Some items could not be fully checked"
            : "Some items could not be checked"
          : nothingToFix
            ? "Everything is in sync"
            : summary.parentsAffected === undefined
              ? `${plural(summary.subitemsAffected, "subitem is", "subitems are")} out of sync`
              : `${plural(summary.parentsAffected, "item")} and ${plural(summary.subitemsAffected, "subitem")} are out of sync`;

      /*
       * The line under a clean-but-incomplete headline says what the result covers. With monday
       * errors the covered count would have to subtract overlapping skip reasons, so it is only
       * stated when it is zero. Without them, `parentsScanned` is the count.
       */
      const boardsNamed = boardsPhrase(summary.boardsAffected, summary.boardNames);
      const boardsClause = boardsNamed === "no boards" ? "this board and the boards it syncs with" : boardsNamed;
      const cleanButIncomplete = (): string => {
        if (hasSkippedItems) {
          return mondaySkipped >= mondayTotal
            ? "No items were checked, so this result cannot say whether anything is in sync."
            : "No out-of-sync values were found in the items that could be checked.";
        }
        if (onlySidesUnverified) return "No out-of-sync values were found on the boards that could be checked.";
        if (summary.parentsScanned === 0) {
          return "No items were checked, so this result cannot say whether anything is in sync.";
        }
        if (summary.parentsScanned === 1) return "No out-of-sync values were found in the one item that could be checked.";
        return `No out-of-sync values were found in the ${plural(summary.parentsScanned, "item")} that could be checked.`;
      };
      const detail =
        nothingToFix && incomplete
          ? cleanButIncomplete()
          : nothingToFix
            ? `No out-of-sync values were found across ${boardsClause}.`
            : `Across ${boardsClause}. Fixing them updates ${plural(summary.columnWrites, "value")}.`;

      /*
       * At most ONE "N of M" sentence: the monday one when it applies, otherwise "N of M items
       * checked" unless every parent was read. The per-reason sentences carry their own counts.
       */
      const countSentence = hasSkippedItems
        ? `${mondaySkipped.toLocaleString()} of ${plural(mondayTotal, "item")} could not be checked because monday returned an error.`
        : allParentsScanned
          ? undefined
          : `${summary.parentsScanned.toLocaleString()} of ${plural(summary.parentsTotal, "item")} checked.`;

      return (
        <div style={CONTAINER_STYLE}>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-xs)" }}>
            <Text type="text1" weight="bold" element="p">
              {headline}
            </Text>
            <Text type="text2" color="secondary" element="p">
              {detail}
            </Text>
          </div>

          {state.finishedAt !== undefined && (
            <div style={QUIET_NOTICE}>
              <Text type="text2" element="p">
                {`Scan finished ${describeElapsed(state.finishedAt, now)}. Results stay available for an hour.`}
              </Text>
            </div>
          )}

          {/*
            "If this keeps happening" because a monday error can recur on every rescan for the
            same items, and "scan again" alone is then advice that does not work.
          */}
          {incomplete && (
            <div style={{ ...WARN_NOTICE, display: "flex", flexDirection: "column", gap: "var(--spacing-xs)" }}>
              {countSentence && (
                <Text type="text2" element="p">
                  {countSentence}
                </Text>
              )}
              {skipCounts.map(({ reason, count }) => (
                <Text key={reason} type="text2" element="p">
                  {skipSentence(reason, count)}
                </Text>
              ))}
              {hasSkippedItems && (
                <Text type="text2" element="p">
                  Try scanning again in a few minutes. If this keeps happening, contact support.
                </Text>
              )}
            </div>
          )}

          <div style={BUTTON_ROW_STYLE}>
            {/*
              The consent gate. This button only ever opens BulkConfirmModal — it must never call
              `confirm()` itself, so a user always passes through the dialog's list, ETA and
              no-undo disclosure before anything is written.
            */}
            {!nothingToFix && (
              <Button onClick={() => setConfirmOpen(true)}>Review and fix</Button>
            )}
            {/*
              Scan again re-runs the READ-ONLY scan by handing the panel back to idle. ALWAYS
              offered, with the "finished N minutes ago" line above as the fact it is judged by:
              a result lives for an hour, and a user who comes back to it late should be able to
              decide it is stale without reloading the page. Primary only when there is nothing
              to fix, because then it is the only thing left to do.
            */}
            <Button kind={nothingToFix ? "primary" : "secondary"} onClick={() => reset()}>
              Scan again
            </Button>
          </div>

          {confirmOpen && (
            <BulkConfirmModal
              summary={summary}
              jobId={state.jobId}
              token={token}
              onConfirm={handleConfirm}
              onCancel={() => setConfirmOpen(false)}
              confirming={confirming}
            />
          )}
        </div>
      );
    }

    case "writing": {
      const { summary, cursor } = state;
      /*
       * Progress from `cursor` over `summary.totalWrites` — entries of the write plan ATTEMPTED
       * over the plan's own length. Not `subitemsAffected` (distinct subitems; a 102-entry plan
       * over 51 subitems once read "90 of 51") and not `report.succeeded / columnWrites`
       * (`succeeded` excludes refusals, so any failed write would stall this short of 100%).
       *
       * Three cases, and the difference between the last two is the point:
       *   > 0        the ratio, clamped (a resumed cursor can exceed the plan it is read beside).
       *   === 0      an empty plan, complete by definition — 100%, not a division by zero.
       *   undefined  UNKNOWN (a record from before `totalWrites` existed), so no percentage at
       *              all. Falsiness cannot separate 0 from absent; only the type can.
       */
      const percent =
        summary.totalWrites === undefined
          ? null
          : summary.totalWrites > 0
            ? Math.min(100, Math.max(0, Math.round((cursor / summary.totalWrites) * 100)))
            : 100;
      return (
        <div style={CONTAINER_STYLE}>
          <div style={ROW_STYLE}>
            <Loader size="small" color="primary" />
            <Text type="text2" color="secondary">
              {`Updating ${plural(summary.columnWrites, "value")} on ${boardsPhrase(summary.boardsAffected, summary.boardNames)}…`}
              {percent === null ? "" : ` ${percent}%`}
            </Text>
          </div>
          {percent !== null && <ProgressBar percent={percent} tone="primary" />}
        </div>
      );
    }

    case "completed": {
      const { report, summary } = state;
      /*
       * `report.succeeded` against `summary.columnWrites`, and both are `(entity, column, board)`
       * CELLS — the same unit the consent dialog named. `report.mutationsSucceeded` is smaller
       * by a factor of the drifting columns per subitem; rendering it here would under-report
       * a completed IRREVERSIBLE write while looking entirely plausible. And a bare "400 values
       * updated" is unreadable without the 412 the user consented to on a dialog that is now
       * closed, so a shortfall is stated as one.
       *
       * `report.failed` is deliberately NOT folded into this arithmetic: its entries are a mix
       * of cells and whole writes, so 400 + failed.length need not reach 412.
       */
      const complete = report.succeeded >= summary.columnWrites;
      const percent =
        summary.columnWrites > 0
          ? Math.min(100, Math.round((report.succeeded / summary.columnWrites) * 100))
          : 100;
      return (
        <div style={CONTAINER_STYLE}>
          {complete ? (
            <div style={GOOD_NOTICE}>
              <Text type="text1" element="p">
                <strong>{`All ${plural(report.succeeded, "value")} updated`}</strong>
                {` on ${boardsPhrase(summary.boardsAffected, summary.boardNames)}.`}
              </Text>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-small)" }}>
              <Text type="text1" weight="bold" element="p">
                {`${report.succeeded} of ${plural(summary.columnWrites, "value")} updated`}
              </Text>
              <ProgressBar percent={percent} tone="positive" />
            </div>
          )}
          <ReportDetails report={report} boardNames={summary.boardNames} />
          {/*
            A new scan is the only verification this feature can offer for a write with no
            undo: it re-reads the boards and shows whatever drift remains. That is why the one
            control here starts a scan rather than merely dismissing the report — a dismiss
            would discard the only account of the write in exchange for nothing.
          */}
          <Text type="text2" color="secondary" element="p">
            Changes made by SIMB cannot be undone. Scan again to confirm the boards now agree.
          </Text>
          <div>
            <Button onClick={() => rescan()}>Scan again to verify</Button>
          </div>
        </div>
      );
    }

    default:
      return null;
  }
};

export default BulkScanSummary;
